import { useEffect } from 'react';

import posthog from 'posthog-js';

import config from '../config';
import { notifyFlags, useFlagsStatus } from './flagsStatus';

type FlagKind = 'release' | 'kill-switch' | 'experiment';

export interface FlagDefinition {
  kind: FlagKind;
  owner: string;
  expiresAt: string;
}

/**
 * Feature flags and experiments in use. The object key is the PostHog flag key, the same one the back uses,
 * and `expiresAt` (YYYY-MM-DD) is when the flag should be cleaned up. Remove the entry on cleanup; the
 * compiler points to every use.
 */
export const FLAGS = {
  'client-bulk-import': { kind: 'release', owner: 'tomas', expiresAt: '2026-12-31' },
  academy: { kind: 'release', owner: 'benjaminf', expiresAt: '2027-02-01' },
} as const satisfies Record<string, FlagDefinition>;

type Flags = typeof FLAGS;
export type FlagKey = { [K in keyof Flags]: Flags[K]['kind'] extends 'experiment' ? never : K }[keyof Flags];
export type ExperimentKey = Exclude<keyof Flags, FlagKey>;

const VARIANTS = ['control', 'test'] as const;
export type Variant = (typeof VARIANTS)[number];

const isVariant = (value: unknown): value is Variant => VARIANTS.includes(value as Variant);

type Override = boolean | string;

const OVERRIDE_PARAM = 'flag';
const OVERRIDES_STORAGE_KEY = 'flag-overrides';
const overrides = new Map<string, Override>();

/**
 * Dev-only overrides, read once at load. `?flag=<key>:on|off|<variant>` keeps the value for the tab;
 * `?flag=<key>:clear` removes it.
 */
function loadDevOverrides(): void {
  if (config.environment === 'production') return;

  try {
    const stored: Record<string, Override> = JSON.parse(sessionStorage.getItem(OVERRIDES_STORAGE_KEY) ?? '{}');
    new URLSearchParams(window.location.search).getAll(OVERRIDE_PARAM).forEach((pair) => {
      const [key, value] = pair.split(':');
      if (!key || !value) return;
      if (value === 'clear') delete stored[key];
      else if (['on', 'true', '1'].includes(value)) stored[key] = true;
      else if (['off', 'false', '0'].includes(value)) stored[key] = false;
      else stored[key] = value;
    });
    sessionStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(stored));
    Object.entries(stored).forEach(([key, value]) => overrides.set(key, value));
  } catch {
    overrides.clear();
  }
}
loadDevOverrides();

export function overrideFlags(values: { [K in FlagKey]?: boolean } & { [K in ExperimentKey]?: Variant }): void {
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined) overrides.set(key, value);
  });
  notifyFlags();
}

export function clearFlagOverrides(): void {
  if (overrides.size === 0) return;
  overrides.clear();
  notifyFlags();
}

export const getFlagOverride = (key: keyof Flags): Override | undefined => overrides.get(key);

export const isKillSwitch = (key: keyof Flags): boolean => (FLAGS[key] as FlagDefinition).kind === 'kill-switch';

/** Returns whether a flag is on. While flags load or when PostHog is off, returns the safe value of its kind. */
export function useFlag(key: FlagKey): boolean {
  const status = useFlagsStatus();
  const override = overrides.get(key);

  if (typeof override === 'boolean') return override;
  if (status !== 'ready') return isKillSwitch(key);
  return posthog.isFeatureEnabled(key, { send_event: false }) ?? isKillSwitch(key);
}

/**
 * Returns the variant the current user sees for an experiment, or `undefined` while flags load.
 * Resolves to `control` when PostHog is off (no cookie consent, timeout, error) or the flag is off.
 */
export function useExperiment(key: ExperimentKey): Variant | undefined {
  const status = useFlagsStatus();
  const override = overrides.get(key);
  const overrideVariant = isVariant(override) ? override : null;
  const flagValue = status === 'ready' ? posthog.getFeatureFlag(key, { send_event: false }) : undefined;
  const isExposed = !overrideVariant && isVariant(flagValue);

  useEffect(() => {
    /* Reading with the event records the exposure once the variant has rendered. */
    if (isExposed) posthog.getFeatureFlag(key);
  }, [key, isExposed, flagValue]);

  if (overrideVariant) return overrideVariant;
  if (status === 'loading') return undefined;
  return isVariant(flagValue) ? flagValue : 'control';
}
